import React, { useEffect, useState } from 'react';
import { BuildingLibraryIcon, XMarkIcon } from '@heroicons/react/24/outline';
import quoteService from '../../services/quoteService';
import patientService from '../../services/patientService';
import { errText } from '../../services/scheduleService';

const input = 'w-24 rounded-md border border-slate-300 px-2 py-1 text-right text-sm tabular-nums focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30';

/**
 * SchemeRates — what each medical aid pays per anaesthetic and clinical unit
 * (optionally for one plan). Quotes compare these with the practice's own fee
 * schedule to estimate the patient's shortfall.
 */
const SchemeRates = () => {
  const [rates, setRates] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [edits, setEdits] = useState({});
  const [adding, setAdding] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    quoteService.schemeRates().then(setRates).catch((e) => setErr(errText(e)));
    patientService.schemes().then(setSchemes).catch(() => {});
  }, []);

  const value = (r, k) => (edits[r.scheme_rate_id]?.[k] ?? r[k]);
  const setValue = (r, k, v) => setEdits((e) => ({ ...e, [r.scheme_rate_id]: { ...e[r.scheme_rate_id], [k]: v } }));

  const run = async (key, fn) => {
    setBusy(key);
    setErr('');
    try {
      setRates(await fn());
      return true;
    } catch (e) {
      setErr(errText(e));
      return false;
    } finally {
      setBusy('');
    }
  };

  const save = async (r) => {
    const ok = await run(`save${r.scheme_rate_id}`, () => quoteService.updateSchemeRate(r.scheme_rate_id, {
      anaesthetic_unit_rate: value(r, 'anaesthetic_unit_rate'), clinical_unit_rate: value(r, 'clinical_unit_rate'),
    }));
    if (ok) setEdits((e) => { const n = { ...e }; delete n[r.scheme_rate_id]; return n; });
  };

  return (
    <section className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-4">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-blue-50 text-blue-700"><BuildingLibraryIcon className="h-6 w-6" /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-slate-800">What medical aids pay</h3>
          <p className="text-sm text-slate-500">
            Rand per unit each scheme pays (from its published anaesthetic rates). Quotes compare this with your fee to show the patient their likely shortfall.
          </p>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="py-1.5 font-semibold">Scheme</th>
              <th className="py-1.5 font-semibold">Plan</th>
              <th className="py-1.5 text-right font-semibold">Anaes. unit</th>
              <th className="py-1.5 text-right font-semibold">Clinical unit</th>
              <th className="w-28" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(rates || []).map((r) => (
              <tr key={r.scheme_rate_id}>
                <td className="py-2 pr-2">
                  <div className="font-semibold text-slate-700">{r.scheme_name}</div>
                  {r.is_placeholder && <span className="rounded bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800">placeholder rates</span>}
                </td>
                <td className="py-2 pr-2 text-xs text-slate-500">{r.plan_option || 'All plans'}</td>
                <td className="py-2 text-right">R <input type="number" min="0" step="0.01" value={value(r, 'anaesthetic_unit_rate')} onChange={(e) => setValue(r, 'anaesthetic_unit_rate', e.target.value)} className={input} /></td>
                <td className="py-2 text-right">R <input type="number" min="0" step="0.01" value={value(r, 'clinical_unit_rate')} onChange={(e) => setValue(r, 'clinical_unit_rate', e.target.value)} className={input} /></td>
                <td className="py-2 pl-2 text-right">
                  {(edits[r.scheme_rate_id] || r.is_placeholder) && (
                    <button onClick={() => save(r)} disabled={!!busy} className="rounded-md bg-brand-navy px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50">
                      {busy === `save${r.scheme_rate_id}` ? 'Saving…' : edits[r.scheme_rate_id] ? 'Save' : 'Confirm'}
                    </button>
                  )}
                  <button onClick={() => run(`rm${r.scheme_rate_id}`, () => quoteService.removeSchemeRate(r.scheme_rate_id))} disabled={!!busy} title="Remove these rates" className="ml-1 rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-600">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {adding && (
              <tr>
                <td className="py-2 pr-2">
                  <select value={adding.scheme_id} onChange={(e) => setAdding({ ...adding, scheme_id: e.target.value })} className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm">
                    <option value="">Choose a scheme…</option>
                    {schemes.map((s) => <option key={s.scheme_id} value={s.scheme_id}>{s.name}</option>)}
                  </select>
                </td>
                <td className="py-2 pr-2"><input value={adding.plan_option} onChange={(e) => setAdding({ ...adding, plan_option: e.target.value })} placeholder="All plans" className="w-28 rounded-md border border-slate-300 px-2 py-1 text-sm" /></td>
                <td className="py-2 text-right">R <input type="number" min="0" step="0.01" value={adding.anaesthetic_unit_rate} onChange={(e) => setAdding({ ...adding, anaesthetic_unit_rate: e.target.value })} className={input} /></td>
                <td className="py-2 text-right">R <input type="number" min="0" step="0.01" value={adding.clinical_unit_rate} onChange={(e) => setAdding({ ...adding, clinical_unit_rate: e.target.value })} className={input} /></td>
                <td className="py-2 pl-2 text-right">
                  <button
                    onClick={async () => { if (await run('add', () => quoteService.addSchemeRate(adding))) setAdding(null); }}
                    disabled={!!busy || !adding.scheme_id}
                    className="rounded-md bg-brand-navy px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50"
                  >
                    {busy === 'add' ? 'Adding…' : 'Add'}
                  </button>
                  <button onClick={() => setAdding(null)} className="ml-1 rounded p-1 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-4 w-4" /></button>
                </td>
              </tr>
            )}
            {rates && rates.length === 0 && !adding && <tr><td colSpan={5} className="py-3 text-center text-slate-400">No medical aid rates yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
      {!adding && (
        <button onClick={() => setAdding({ scheme_id: '', plan_option: '', anaesthetic_unit_rate: '', clinical_unit_rate: '' })} className="mt-3 text-sm font-semibold text-brand-teal hover:underline">
          + Rates for a medical aid
        </button>
      )}
    </section>
  );
};

export default SchemeRates;
